// Os providers da casca, na ordem em que dependem uns dos outros: tema
// (só CSS), consultas (o cache de servidor), sessão (quem é e em que loja
// está) e tempo real (que invalida o cache). Sessão e tempo real ficam na
// Casca, que só as rotas do painel usam: entrar, criar conta e convite são
// públicas.
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { api, irParaLogin } from '../shared/api/api';
import type { QuemSou } from '../shared/api/tipos';
import { usuarioAtual } from '../shared/auth/firebase';
import { lojaLembrada, useLoja } from '../shared/estado/loja';
import { TemaProvider } from '../shared/tema/tema';
import { liberarBoot } from '../shared/ui/boot';
import { TempoRealProvider } from '../shared/tempo-real/tempo-real';
import { FerramentasDoNavegador } from '../features/conectar-ia/webmcp';

export const consultas = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

/** A loja pedida na URL (`?tenant=`) vale mais que a lembrada; o backend decide se a pessoa a alcança. */
function lojaPedida(): string | null {
  return new URLSearchParams(location.search).get('tenant') || lojaLembrada();
}

export function Sessao({ children }: { children: ReactNode }) {
  const entrar = useLoja((s) => s.entrar);
  const pronta = useLoja((s) => s.tenantId !== null);
  const quem = useQuery({
    queryKey: ['me', lojaPedida()],
    queryFn: async () => {
      if (!(await usuarioAtual())) { irParaLogin(); return null; }
      const pedida = lojaPedida();
      return api.get<QuemSou>('/api/me' + (pedida ? `?tenant=${encodeURIComponent(pedida)}` : ''));
    },
    staleTime: Infinity,
  });
  useEffect(() => { if (quem.data) entrar(quem.data); }, [quem.data, entrar]);
  // O erro não pode ficar atrás da cortina de abertura.
  useEffect(() => { if (quem.isError) liberarBoot(); }, [quem.isError]);

  if (quem.isError) {
    return <div className="vidro" style={{ margin: 40, padding: 24 }}><h2>Não deu pra abrir o painel</h2><p className="apoio">{(quem.error as Error).message}</p><button type="button" className="botao" onClick={() => quem.refetch()}>Tentar de novo</button></div>;
  }
  if (!pronta) return <div className="apoio" style={{ padding: 40 }} aria-busy="true">Abrindo o painel…</div>;
  return <>{children}</>;
}

/** Tema e cache: o que TODA rota tem, inclusive as públicas (entrar, criar conta, convite). */
export function Providers({ children }: { children: ReactNode }) {
  return (
    <TemaProvider>
      <QueryClientProvider client={consultas}>{children}</QueryClientProvider>
    </TemaProvider>
  );
}

/** O painel de verdade: sessão numa loja, o tempo real dela e as ferramentas que o agente do navegador enxerga (WebMCP). */
export function Casca({ children }: { children: ReactNode }) {
  return (
    <Sessao>
      <TempoRealProvider>{children}<FerramentasDoNavegador /></TempoRealProvider>
    </Sessao>
  );
}
